import React, {FC} from 'react';
import styled from 'styled-components';

import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {isLinkedConversationActivity} from '../../../../../models/api/activityModel';
import {getActivities} from '../../../../../store/data/dataSelectors';
import {CommentHeaderIcon} from '../../activity/comment/commentHeaderIcon';
import {SynopsisCellHeaderContainer} from './synopsisCellHeaderContainer';
import {SynopsisConversationPreview} from './synopsisConversationPreview';
import {SynopsisPinnedActivityItem} from './synopsisHelpers';
import {useSynopsisViewerContext} from './synopsisViewerContext';

interface SynopsisLinkedConversationProps {
  item: SynopsisPinnedActivityItem;
  panelId: ViewerPanelId;
}

const StyledCellDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  border-radius: 12px;
  background-color: ${(p) => p.theme.message.readCommentBackground};
`;

const StyledCellDetailButton = styled(ChromelessButton)`
  position: relative;
  padding: 4px 8px;
  border-radius: 8px;
  margin: 0 4px;

  background-color: ${(p) => p.theme.message.readCommentBackground};

  &:hover {
    background-color: ${(p) => p.theme.message.readCommentBackground};
  }
`;

const StyledInnerDiv = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  grid-template-areas: 'status content';
  grid-gap: 8px;
`;

const StyledStatusDiv = styled.div`
  grid-area: status;
  margin-top: 2px;
`;

const StyledBodyWrapper = styled.div`
  background-color: ${(p) => p.theme.alphas.gray10};
  border-radius: 8px;
  padding: 4px 8px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.black30};
  }
`;

export const SynopsisLinkedConversation: FC<SynopsisLinkedConversationProps> = ({item, panelId}) => {
  const theme = useUserTheme();
  const {onJumpToItem, namespace} = useSynopsisViewerContext();

  const activity = useAppSelector((state) => getActivities(state)[item.activityId]);
  if (!isLinkedConversationActivity(activity) || !activity.linkedConversationId) {
    return null;
  }

  return (
    <StyledCellDiv>
      <SynopsisCellHeaderContainer
        teammateIds={activity.pinnedByTeammateId ? [activity.pinnedByTeammateId] : []}
        timestamp={item.timestamp}
        namespace={namespace}
        panelId={panelId}
      />
      <StyledCellDetailButton
        onClick={() => onJumpToItem({activityId: item.activityId, itemType: item.type})}
      >
        <StyledInnerDiv>
          <StyledStatusDiv>
            <CommentHeaderIcon iconName="linkedConversationsTiny" backgroundColor={theme.greys.shade80} />
          </StyledStatusDiv>
          <StyledBodyWrapper>
            <SynopsisConversationPreview
              conversationId={activity.linkedConversationId}
              panelId={panelId}
              trackEventObjectData={{
                related_type: 'linked_conversation',
                activity_id: item.activityId,
                type: item.type,
              }}
            />
          </StyledBodyWrapper>
        </StyledInnerDiv>
      </StyledCellDetailButton>
    </StyledCellDiv>
  );
};
